/**
 * videoExport.ts
 *
 * Client-side video export with burned-in subtitles using ffmpeg.wasm.
 *
 * Facts (verified):
 * - `@ffmpeg/core-mt` uses SharedArrayBuffer and therefore requires a
 *   cross-origin-isolated page. The export falls back to `@ffmpeg/core` when
 *   the browser/site cannot provide that environment or the MT core fails.
 * - The core includes libass + freetype but NO fontconfig: `ass=` works and
 *   fonts are resolved from `fontsdir` by their internal family name. The
 *   bundled ProximaNova-Bold.ttf's libass-visible family is exactly
 *   'Proxima Nova Rg' (TTF nameID 1; see srtToAss.ts), which is what the
 *   generated ASS style requests. The editor's system-font option uses this
 *   same bundled face during export because browser code cannot read host
 *   system fonts.
 *
 * Each call creates a FRESH FFmpeg instance and terminates it when done:
 * a terminated instance is unusable — never reuse one across calls.
 */
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
// @ffmpeg/core's package `exports` only exposes the '.' and './wasm' subpaths —
// deep `dist/esm/*` imports fail Vite's exports resolution. The `?url` suffix
// makes Vite emit each file as a cache-busted asset and hand us its URL; the
// import condition resolves to the ESM core (dist/esm/ffmpeg-core.js), whose
// default export is what the @ffmpeg/ffmpeg worker dynamic-imports.
import singleCoreURL from '@ffmpeg/core?url';
import singleWasmURL from '@ffmpeg/core/wasm?url';
import multiCoreURL from '@ffmpeg/core-mt?url';
import multiWasmURL from '@ffmpeg/core-mt/wasm?url';
import multiWorkerURL from '@ffmpeg/core-mt/worker?url';
import type { SrtEntry } from '@lambda-libs/types';
import { srtToAss } from './srtToAss';
import type { CaptionStyleOptions } from './srtToAss';

export type ExportPreset = '1080p' | '720p' | 'original';
/** Output quality → target video bitrate (VBR/ABR around the target, CapCut-style). */
export type ExportBitrate = 'lower' | 'recommended' | 'higher';
/** Output frame rate; 'auto' keeps the source frame rate (no fps filter). */
export type ExportFps = 'auto' | 24 | 25 | 30 | 50 | 60;

export interface BrowserEncoderCapabilities {
  crossOriginIsolated: boolean;
  hasSharedArrayBuffer: boolean;
  hardwareConcurrency: number;
}

export interface BrowserEncoderMode {
  multithread: boolean;
  threads: number;
}

const MAX_BROWSER_ENCODING_THREADS = 6;

/**
 * Select a conservative browser encoder mode. Six threads gives the MT core
 * useful parallelism without letting high-core-count desktops multiply the
 * already-large FFmpeg.wasm memory footprint.
 */
export function resolveBrowserEncoderMode(
  capabilities: BrowserEncoderCapabilities
): BrowserEncoderMode {
  const hardwareThreads =
    Number.isFinite(capabilities.hardwareConcurrency) && capabilities.hardwareConcurrency > 0
      ? Math.floor(capabilities.hardwareConcurrency)
      : 1;
  const threads = Math.min(MAX_BROWSER_ENCODING_THREADS, hardwareThreads);
  const multithread =
    capabilities.crossOriginIsolated && capabilities.hasSharedArrayBuffer && threads >= 2;
  return { multithread, threads: multithread ? threads : 1 };
}

export function getBrowserEncoderMode(): BrowserEncoderMode {
  return resolveBrowserEncoderMode({
    crossOriginIsolated: globalThis.crossOriginIsolated === true,
    hasSharedArrayBuffer: typeof globalThis.SharedArrayBuffer !== 'undefined',
    hardwareConcurrency:
      typeof navigator !== 'undefined' && Number.isFinite(navigator.hardwareConcurrency)
        ? navigator.hardwareConcurrency
        : 1,
  });
}

export interface ExportVideoOptions {
  /** Same-origin URL of the source video (e.g. CloudFront `files/*` — secretId is the token, no auth). */
  videoUrl: string;
  entries: SrtEntry[];
  captionStyle: CaptionStyleOptions;
  preset: ExportPreset;
  /** Quality preset → target video bitrate (VBR). Defaults to 'recommended' (~CapCut default). */
  bitrate?: ExportBitrate;
  /** Output frame rate; 'auto' (default) keeps the source fps. */
  fps?: ExportFps;
  /**
   * URL of the ProximaNova-Bold.ttf asset — pass the result of
   * `import fontUrl from '@/assets/fonts/ProximaNova-Bold.ttf?url'`
   * (Vite emits the asset; the same TTF powers the 'Bold' @font-face).
   */
  fontFileUrl: string;
  /** Progress 0–100. */
  onProgress?: (percent: number) => void;
  /** Cancels the export (terminates the ffmpeg worker → rejects with AbortError). */
  signal?: AbortSignal;
}

const PRESET_SHORT_EDGE: Record<'1080p' | '720p', number> = {
  '1080p': 1080,
  '720p': 720,
};

/** CapCut-style target bitrates, anchored at 1080p and scaled ∝ height² (±clamp). */
const BITRATE_KBPS_AT_1080: Record<ExportBitrate, number> = {
  lower: 6000,
  recommended: 12000,
  higher: 20000,
};
const REF_HEIGHT = 1080;

export function targetBitrateKbps(outReferenceEdge: number, quality: ExportBitrate): number {
  const base = BITRATE_KBPS_AT_1080[quality];
  const edge = outReferenceEdge > 0 ? outReferenceEdge : REF_HEIGHT;
  const scaled = Math.round((base * (edge / REF_HEIGHT) ** 2) / 100) * 100;
  return Math.max(1500, Math.min(60000, scaled));
}

export interface ExportGeometry {
  width: number;
  height: number;
  scaleFilter: string | null;
  referenceEdge: number;
}

/**
 * Resolve a conventional resolution preset for either orientation.
 * "1080p" means 1920×1080 landscape or 1080×1920 portrait: the short edge
 * is capped at 1080 while preserving aspect ratio, and smaller sources are
 * never upscaled.
 */
export function resolveExportGeometry(
  sourceWidth: number,
  sourceHeight: number,
  preset: ExportPreset
): ExportGeometry {
  const validWidth = Number.isFinite(sourceWidth) && sourceWidth > 0 ? Math.round(sourceWidth) : 0;
  const validHeight =
    Number.isFinite(sourceHeight) && sourceHeight > 0 ? Math.round(sourceHeight) : 0;

  if (!validWidth || !validHeight) {
    const fallback = preset === 'original' ? PRESET_SHORT_EDGE['1080p'] : PRESET_SHORT_EDGE[preset];
    return { width: 0, height: 0, scaleFilter: null, referenceEdge: fallback };
  }

  const sourceShortEdge = Math.min(validWidth, validHeight);
  if (preset === 'original' || sourceShortEdge <= PRESET_SHORT_EDGE[preset]) {
    return {
      width: validWidth,
      height: validHeight,
      scaleFilter: null,
      referenceEdge: sourceShortEdge,
    };
  }

  const target = PRESET_SHORT_EDGE[preset];
  if (validWidth <= validHeight) {
    const height = Math.max(2, Math.round((validHeight * target) / validWidth / 2) * 2);
    return {
      width: target,
      height,
      scaleFilter: `scale=${target}:-2`,
      referenceEdge: target,
    };
  }

  const width = Math.max(2, Math.round((validWidth * target) / validHeight / 2) * 2);
  return {
    width,
    height: target,
    scaleFilter: `scale=-2:${target}`,
    referenceEdge: target,
  };
}

export interface VideoStreamInfo {
  /** Source coded-frame width in px; 0 when unparsable. */
  width: number;
  /** Source coded-frame height in px; 0 when unparsable (→ srtToAss falls back to 1080p). */
  height: number;
  hasAudio: boolean;
}

/**
 * Parse the log of a probe run (`ffmpeg -i in.mp4`) for the source frame
 * dimensions and whether an audio stream exists. Typical line:
 *   Stream #0:0(und): Video: h264 (High), yuv420p(tv, bt709), 1920x1080 [SAR 1:1 DAR 16:9], …
 */
export function parseStreamInfo(log: string): VideoStreamInfo {
  let width = 0;
  let height = 0;
  let hasAudio = false;
  for (const line of log.split('\n')) {
    if (line.indexOf('Stream #') === -1) continue;
    if (line.indexOf('Video:') !== -1) {
      if (!width || !height) {
        const match = line.match(/(\d{2,5})x(\d{2,5})/); // first WxH is the coded size
        if (match) {
          width = parseInt(match[1], 10);
          height = parseInt(match[2], 10);
        }
      }
    } else if (line.indexOf('Audio:') !== -1) {
      hasAudio = true;
    }
  }
  return { width, height, hasAudio };
}

const makeAbortError = (): Error => {
  const err = new Error('Video export cancelled');
  err.name = 'AbortError';
  return err;
};

/**
 * Burn the SRT captions into the video and return the result as an mp4 Blob.
 * Runs entirely in the browser (MEMFS); single-thread encoding is slow
 * (~0.2–0.5× realtime) and long videos may exhaust browser memory — the UI
 * surfaces that warning before calling.
 */
export async function exportVideoWithSubtitles({
  videoUrl,
  entries,
  captionStyle,
  preset,
  bitrate = 'recommended',
  fps = 'auto',
  fontFileUrl,
  onProgress,
  signal,
}: ExportVideoOptions): Promise<Blob> {
  if (signal?.aborted) throw makeAbortError();

  let ffmpeg = new FFmpeg();
  const logs: string[] = [];
  const attachLogger = () => {
    ffmpeg.on('log', ({ message }: { message: string }) => {
      logs.push(message);
    });
  };
  attachLogger();

  const onAbort = () => {
    try {
      ffmpeg.terminate();
    } catch {
      /* already dead */
    }
  };
  signal?.addEventListener('abort', onAbort, { once: true });

  try {
    let encoderMode = getBrowserEncoderMode();
    if (encoderMode.multithread) {
      try {
        await ffmpeg.load({
          coreURL: multiCoreURL,
          wasmURL: multiWasmURL,
          workerURL: multiWorkerURL,
        });
      } catch (error) {
        if (signal?.aborted) throw makeAbortError();
        // A browser may report cross-origin isolation but still reject a WASM
        // pthread worker. Recreate FFmpeg because a failed/terminated instance
        // is not reusable, then continue with the proven single-thread core.
        console.warn('Multithreaded FFmpeg unavailable; falling back to single thread.', error);
        try {
          ffmpeg.terminate();
        } catch {
          /* failed load may already have stopped the worker */
        }
        ffmpeg = new FFmpeg();
        attachLogger();
        encoderMode = { multithread: false, threads: 1 };
        await ffmpeg.load({ coreURL: singleCoreURL, wasmURL: singleWasmURL });
      }
    } else {
      await ffmpeg.load({ coreURL: singleCoreURL, wasmURL: singleWasmURL });
    }

    // --- stage inputs in MEMFS -------------------------------------------
    const videoResponse = await fetch(videoUrl);
    if (!videoResponse.ok) {
      throw new Error(`Could not download source video (HTTP ${videoResponse.status})`);
    }
    const videoData = new Uint8Array(await videoResponse.arrayBuffer());
    const fontData = await fetchFile(fontFileUrl);

    await ffmpeg.writeFile('in.mp4', videoData);
    await ffmpeg.createDir('fonts'); // MEMFS does not auto-create directories
    await ffmpeg.writeFile('fonts/ProximaNova-Bold.ttf', fontData);

    // --- probe: source frame size + audio presence -----------------------
    logs.length = 0;
    await ffmpeg.exec(['-i', 'in.mp4']); // exits non-zero by design (no output file)
    const { width: sourceWidth, height: sourceHeight, hasAudio } = parseStreamInfo(logs.join('\n'));

    // --- subtitles --------------------------------------------------------
    // Both dimensions are required: if PlayResX stayed fixed at 1920 while
    // PlayResY followed the source, libass would stretch/squash glyphs on every
    // non-1920×1080 frame (the exported Bold text looked wider on 1440p/4K).
    const ass = srtToAss(entries, captionStyle, {
      width: sourceWidth,
      height: sourceHeight,
    });
    await ffmpeg.writeFile('subs.ass', ass);

    let vf = 'ass=subs.ass:fontsdir=fonts';
    const geometry = resolveExportGeometry(sourceWidth, sourceHeight, preset);
    if (geometry.scaleFilter) vf += `,${geometry.scaleFilter}`;
    if (fps !== 'auto') {
      vf += `,fps=${fps}`;
    }

    // Attached after the probe so onProgress only sees the real encode.
    ffmpeg.on('progress', ({ progress }: { progress: number }) => {
      if (onProgress) onProgress(progress * 100);
    });

    const kbps = targetBitrateKbps(geometry.referenceEdge, bitrate);
    const args = [
      '-i', 'in.mp4',
      '-map', '0:v:0',
      '-map', '0:a:0?', // optional audio mapping: sources without audio must not fail
      '-vf', vf,
      '-c:v', 'libx264',
      ...(encoderMode.multithread ? ['-threads', String(encoderMode.threads)] : []),
      '-preset', 'veryfast',
      // Target-bitrate VBR (CapCut-style) instead of CRF: bigger, predictable files.
      '-b:v', `${kbps}k`,
      '-maxrate', `${Math.round(kbps * 1.5)}k`,
      '-bufsize', `${Math.round(kbps * 2)}k`,
      '-pix_fmt', 'yuv420p',
      ...(hasAudio ? ['-c:a', 'aac', '-b:a', '192k'] : []),
      '-movflags', '+faststart',
      'out.mp4',
    ];

    logs.length = 0;
    const exitCode = await ffmpeg.exec(args);
    if (signal?.aborted) throw makeAbortError();
    if (exitCode !== 0) {
      throw new Error('ffmpeg exited with code ' + exitCode + '\n' + logs.slice(-15).join('\n'));
    }

    const out = (await ffmpeg.readFile('out.mp4')) as Uint8Array;
    // Copy out of wasm memory before the instance is terminated below.
    return new Blob([new Uint8Array(out)], { type: 'video/mp4' });
  } catch (err) {
    if (signal?.aborted) throw makeAbortError();
    throw err;
  } finally {
    signal?.removeEventListener('abort', onAbort);
    try {
      ffmpeg.terminate(); // frees the worker + MEMFS; instance unusable afterwards
    } catch {
      /* already dead */
    }
  }
}
